import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CaretRight, CircleNotch, Play, SpeakerHigh, Stop } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAudition } from "@/hooks/useAudition";
import { btnGhost, inputCls, labelCls } from "@/lib/ui";

// Voice settings the server lets an audition try before they are saved.
const VOICE_KEYS = [
  "elevenlabs_voice_id", "elevenlabs_model", "elevenlabs_speed", "elevenlabs_stability", "elevenlabs_similarity",
  "sarvam_speaker", "sarvam_tts_model", "sarvam_language", "sarvam_pace", "sarvam_temperature",
];
const pickVoiceSettings = (draft, extra = {}) => ({ ...Object.fromEntries(VOICE_KEYS.map((k) => [k, draft[k]])), ...extra });

// ElevenLabs sorts its voices by use case; Conversational is the one that suits talking back in a meeting.
const USE_CASES = [
  ["conversational", "Conversational"], ["narrative_story", "Narration"], ["informative_educational", "Informative"],
  ["social_media", "Social media"], ["advertisement", "Advertising"], ["characters_animation", "Characters"], ["all", "Any"],
];

const PlayIcon = ({ phase }) =>
  phase === "loading" ? <CircleNotch size={13} className="animate-spin" /> : phase === "playing" ? <Stop size={13} weight="fill" /> : <Play size={13} weight="fill" />;

const chip = (on) =>
  `font-mono text-[11px] px-2.5 py-1 border transition-colors ${on ? "bg-[var(--o-fg)] text-[var(--o-bg)] border-[var(--o-fg)]" : "border-[var(--o-border)] text-[var(--o-muted)] hover:text-[var(--o-fg)] hover:border-[var(--o-border-strong)]"}`;

const VoiceRow = ({ v, draft, setDraft, audition, added, onAdded }) => {
  const [adding, setAdding] = useState(false);
  // A Library voice becomes a copy on the account, which may carry a different id than the listing.
  const inUse = [v.voice_id, added[v.voice_id]].includes(draft.elevenlabs_voice_id);
  const phase = (key) => (audition.state.key === key ? audition.state.phase : "idle");

  const use = async () => {
    setAdding(true);
    try {
      const { data } = await api.post("/admin/tts/voices/use", { voice_id: v.voice_id, public_owner_id: v.public_owner_id || "", name: v.name });
      onAdded(v.voice_id, data.voice_id);
      setDraft((d) => ({ ...d, elevenlabs_voice_id: data.voice_id }));
      toast.success(`${v.name} selected. Press “Hear ElevenLabs”, then Save changes.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAdding(false);
    }
  };

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 border-b border-[var(--o-border-soft)] last:border-b-0" data-testid={`voice-row-${v.voice_id}`}>
      <div className="min-w-0 flex-1 basis-56">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold truncate">{v.name}</span>
          {inUse && <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-[#10B981]/50 text-[#10B981]">in use</span>}
        </div>
        <div className="font-mono text-[11px] text-[var(--o-muted2)] mt-0.5">{v.tags.join(" · ")}</div>
        {v.description && <p className="text-[11px] text-[var(--o-muted3)] mt-1 line-clamp-2">{v.description}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {v.preview_url && (
          <button type="button" onClick={() => audition.play(`preview-${v.voice_id}`, async () => ({ src: v.preview_url }))} className={`${btnGhost} !px-3 !py-1.5 text-xs`} data-testid={`voice-preview-${v.voice_id}`}>
            <PlayIcon phase={phase(`preview-${v.voice_id}`)} /> Preview
          </button>
        )}
        <button type="button" onClick={use} disabled={adding || inUse} className={`${btnGhost} !px-3 !py-1.5 text-xs`} data-testid={`voice-use-${v.voice_id}`}>
          {adding ? <CircleNotch size={13} className="animate-spin" /> : null} {v.source === "library" ? "Add & use" : "Use"}
        </button>
      </div>
    </li>
  );
};

const VoiceFinder = ({ draft, setDraft, audition }) => {
  const [source, setSource] = useState("library");
  const [useCase, setUseCase] = useState("conversational");
  const [gender, setGender] = useState("male");
  const [search, setSearch] = useState("");
  const [state, setState] = useState({ loading: false, error: "", voices: null });
  const [added, setAdded] = useState({}); // listing id -> id of the copy on this account

  const find = useCallback(async (q) => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.get("/admin/tts/voices", { params: { source, use_case: useCase, gender, search: q } });
      setState({ loading: false, error: "", voices: data.voices });
    } catch (e) {
      setState({ loading: false, error: errMsg(e), voices: null });
    }
  }, [source, useCase, gender]);

  // Re-run when a filter changes; typed text waits for Enter so every keystroke is not a request.
  useEffect(() => { find(search); }, [find]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="px-5 pb-5 space-y-4" data-testid="voice-finder">
      <p className="text-xs text-[var(--o-muted2)] max-w-2xl">
        ElevenLabs sorts voices by what they are made for. For a voice that talks back in a meeting start with Conversational, then listen
        to a few. Voice weight comes from the voice itself, so this is where a deeper, firmer sound is chosen.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <span className={labelCls}>Where</span>
        {[["library", "Voice Library"], ["mine", "My voices"]].map(([v, l]) => (
          <button key={v} type="button" className={chip(source === v)} onClick={() => setSource(v)} data-testid={`voice-source-${v}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="voice-use-cases">
        <span className={labelCls}>Made for</span>
        {USE_CASES.map(([v, l]) => (
          <button key={v} type="button" className={chip(useCase === v)} onClick={() => setUseCase(v)} data-testid={`voice-usecase-${v}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <select className={`${inputCls} !w-auto font-mono text-xs`} value={gender} onChange={(e) => setGender(e.target.value)} aria-label="Voice gender" data-testid="voice-gender">
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="">Any</option>
        </select>
        <input
          className={`${inputCls} !w-auto flex-1 min-w-[180px] max-w-xs text-xs`} value={search} placeholder="Search: deep, firm, Indian…"
          onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && find(search)}
          autoComplete="off" spellCheck={false} data-testid="voice-search"
        />
        <button type="button" className={`${btnGhost} !py-2 text-xs`} onClick={() => find(search)} data-testid="voice-search-button">Search</button>
      </div>
      {state.loading && <div className="h-16 shimmer" />}
      {state.error && <p className="text-xs text-[#EF4444] break-words" data-testid="voice-error">{state.error}</p>}
      {state.voices && !state.loading && (state.voices.length ? (
        <ul className="border border-[var(--o-border)]" data-testid="voice-results">
          {state.voices.map((v) => <VoiceRow key={`${v.source}-${v.voice_id}`} v={v} draft={draft} setDraft={setDraft} audition={audition} added={added} onAdded={(from, to) => setAdded((a) => ({ ...a, [from]: to }))} />)}
        </ul>
      ) : (
        <p className="text-xs text-[var(--o-muted2)]" data-testid="voice-empty">
          No voices match. {source === "mine" ? "Your account may not hold any for this use. Try the Voice Library." : "Try a broader use case or another search."}
        </p>
      ))}
    </div>
  );
};

// Shown inside the Read aloud section: hear the voice with the numbers currently on screen (saved or not),
// and find a different ElevenLabs voice by use case.
export const VoiceTools = ({ draft, setDraft, cfg }) => {
  const audition = useAudition();
  const phase = (key) => (audition.state.key === key ? audition.state.phase : "idle");
  const hear = (provider) =>
    audition.play(`sample-${provider}`, async () => {
      const { data } = await api.post("/admin/tts/sample", { provider, settings: pickVoiceSettings(draft) }, { responseType: "blob", timeout: 60000 });
      const src = URL.createObjectURL(data);
      return { src, revoke: src };
    });
  const voices = Object.entries(cfg.voices || {});
  const [finderOpen, setFinderOpen] = useState(false); // the finder only calls ElevenLabs once it is opened

  return (
    <>
      <div className="px-5 pb-5 flex flex-wrap items-center gap-3" data-testid="voice-audition">
        {voices.map(([id, v]) => (
          <button key={id} type="button" onClick={() => hear(id)} disabled={!v.configured} className={btnGhost} title={v.configured ? "Plays a short sample with the values on screen, even if unsaved" : "Add its API key above first"} data-testid={`hear-${id}`}>
            {phase(`sample-${id}`) === "idle" ? <SpeakerHigh size={15} /> : <PlayIcon phase={phase(`sample-${id}`)} />} Hear {v.label}
          </button>
        ))}
        <span className="text-[11px] text-[var(--o-muted3)]">Uses what is on screen, saved or not. A few seconds of credit each time.</span>
      </div>
      <details className="group border-t border-[var(--o-border-soft)]" open={finderOpen} onToggle={(e) => setFinderOpen(e.currentTarget.open)} data-testid="voice-finder-section">
        <summary className="cursor-pointer list-none px-5 py-3 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--o-muted2)] hover:text-[var(--o-fg)]" data-testid="voice-finder-toggle">
          <CaretRight size={11} className="transition-transform group-open:rotate-90" /> Find an ElevenLabs voice · by use
        </summary>
        {finderOpen && <VoiceFinder draft={draft} setDraft={setDraft} audition={audition} />}
      </details>
    </>
  );
};
